import { useSyncHorizontalScroll } from '../../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../../hooks/useCalculateFetchItems';
import { setMachineNoteList } from '../../../redux/machineNoteListSlice';
import { useScrollFetchData } from '../../../hooks/useScrollFetchData';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { ListNavigationProvider, useListPageNavigation } from '../../../hooks/listNavigation';
import { useState, useRef, useEffect, useCallback } from 'react';
import MachineNoteListViewData from './machineNoteListViewData';
import CustomHeaderCell from '../../../utils/customHeaderCell';
import MachineNoteFiltration from './machineNoteFiltration';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { AddMachineNote } from './addMachineNote';
import { PersistedFixedSizeList as FixedSizeList } from "../../../hooks/listNavigation";
import { useNavigate } from 'react-router';
import NoRecord from '../../NoRecord';
import { debounce } from 'lodash';
import axios from 'axios';
import '../../../css/machines/readMachineNotesTab.css';

const defaultColumnWidth = {
    status: 150,
    type: 150,
    created_by: 150,
    email: 150,
    created_at: 200,
    note: 600,
};

const ReadMachineNotesTab = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const notes = useSelector((state) => state?.machineNoteList) || [];
    const machineDetailsData = useSelector((state) => state.MachineDetailsData) || {};
    const hasMachineReadPermission = useHasPermission('machine_view');

    const limit = 30;
    const [loading, setLoading] = useState(true);
    const [fetchedNotes, setFetchedNotes] = useState('0');
    const [openAddNote, setOpenAddNote] = useState(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [machineNoteFiltration, setMachineNoteFiltration] = useState(
        JSON.parse(localStorage.getItem('machineNoteFiltration')) ||
        { search: '', date: '', status: '', type: '' }
    );
    const [sortBy, setSortBy] = useState(
        JSON.parse(localStorage.getItem('machineNoteSortBy')) ||
        { status: null, type: null, created_by: null, created_at: null, note: null }
    );
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'machine-notes',
        pathname: String(machineDetailsData.id || ''),
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: machineNoteFiltration,
        resetOnSort: sortBy,
        ready: !loading,
    });

    const [machineNoteColumnWidth, setMachineNoteColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('machineNoteColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const note_status = [
        { id: 'Active', name: 'Active' },
        { id: 'Annulee', name: 'Annulée' },
        { id: 'Prise_en_compte', name: 'Prise en compte' },
    ];

    const note_type = [
        { id: 'Securite', name: 'Sécurité' },
        { id: 'Maintenance', name: 'Maintenance' },
        { id: 'Information', name: 'Information' },
        { id: 'Autre', name: 'Autre' },
    ];

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setMachineNoteColumnWidth({ ...machineNoteColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('machineNoteFiltration', JSON.stringify(machineNoteFiltration));
    }, [machineNoteFiltration]);

    useEffect(() => {
        localStorage.setItem('machineNoteSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('machineNoteColumnWidth', JSON.stringify(machineNoteColumnWidth));
    }, [machineNoteColumnWidth]);

    const fetchNotes = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('machine-note', {
                params: {
                    machine_id: machineDetailsData.id,
                    offset: adjustedOffset,
                    limit,
                    filters: machineNoteFiltration,
                    sortBy
                }
            });

            if (response.status === 200 || response.status === 201) {
                // Prepare the updated notes array
                const updatedNotes = [];
                response?.data?.machine_notes?.forEach((file, index) => {
                    updatedNotes[adjustedOffset + index] = file;
                });

                // Dispatch the updated state to the slice
                dispatch(setMachineNoteList({
                    notes: updatedNotes, // Set the notes array
                    totalNotes: response.data.totalNotesCount, // Set total notes count
                    filteredNotesCount: response.data.filteredNotesCount, // Set filtered notes count
                }));

                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching notes:', error);
            CustomErrorLogger(error, 'fetch notes in notes page');
        }
    }, [offset, machineDetailsData.id, machineNoteFiltration, sortBy, dispatch]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const handleResetFilters = useCallback(() => {
        setMachineNoteFiltration({
            search: '',
            status: '',
            date: '',
            type: '',
        });
    }, [setMachineNoteFiltration])


    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(notes.filteredNotesCount, setFetchedNotes, 70, 400);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setFetchedNotes, notes.filteredNotesCount, 70, 400);

    const handleNoteClick = useCallback((noteData) => {
        navigate('remarque_details', { state: noteData.id })
    }, [navigate]);

    const Row = ({ index, style, data }) => {
        const { notes, handleNoteClick } = data;
        const note = index === 0 ? notes.notes[0] : notes.notes[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <MachineNoteListViewData
                    note={note}
                    columnWidth={machineNoteColumnWidth}
                    handleNoteClick={handleNoteClick}
                />
            </Box>
        );
    };

    return (
        <ListNavigationProvider value={navigationValue}>
        <Box>
            {loading ?
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress
                        sx={{
                            '& .MuiCircularProgress-circle': { stroke: '#EA5E00' },
                        }}
                    />
                </Box>
                :
                notes.totalNotes === 0 ?
                    <NoRecord
                        description='Aucun remarque ajouté pour le moment'
                        buttonTitle='Ajouter une remarque'
                        handleOpenDialog={() => setOpenAddNote(true)}
                        hasPermission={hasMachineReadPermission && !machineDetailsData?.is_archived}
                    />
                    : notes.totalNotes > 0 &&
                    <Box>
                        <MachineNoteFiltration
                            totalNotes={notes.totalNotes}
                            fetchedNotes={fetchedNotes}
                            handleResetFilters={handleResetFilters}
                            filteredNotesCount={notes.filteredNotesCount}
                            machineNoteFiltration={machineNoteFiltration}
                            setMachineNoteFiltration={setMachineNoteFiltration}
                        />
                        <Box className='table'>
                            <Box className='tableHead' ref={tableHeadRef}>
                                <CustomHeaderCell
                                    label='Statut'
                                    columnKey='status'
                                    isFixed={true}
                                    sortBy={sortBy}
                                    handleSort={handleSort}
                                    sortable={true}
                                    isFilter={true}
                                    value={machineNoteFiltration.status}
                                    onChange={(e) => { setMachineNoteFiltration({ ...machineNoteFiltration, status: e.target.value }) }}
                                    options={note_status}
                                    backgroundColor='#F6F6F6'
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell
                                    label='Type'
                                    columnKey='type'
                                    backgroundColor='#F6F6F6'
                                    sortBy={sortBy}
                                    handleSort={handleSort}
                                    sortable={true}
                                    isFilter={true}
                                    value={machineNoteFiltration.type}
                                    onChange={(e) => { setMachineNoteFiltration({ ...machineNoteFiltration, type: e.target.value }) }}
                                    options={note_type}
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell
                                    label='Créé par'
                                    columnKey='created_by'
                                    backgroundColor='#F6F6F6'
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell
                                    label='Adresse e-mail'
                                    columnKey='email'
                                    backgroundColor='#F6F6F6'
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell
                                    label='Créé le'
                                    columnKey='created_at'
                                    backgroundColor='#F6F6F6'
                                    sortBy={sortBy}
                                    handleSort={handleSort}
                                    sortable={true}
                                    isFilter={true}
                                    value={machineNoteFiltration.date}
                                    onChange={(e) => { setMachineNoteFiltration({ ...machineNoteFiltration, date: e }) }}
                                    options='date'
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell
                                    label='Remarque'
                                    columnKey='note'
                                    backgroundColor='#F6F6F6'
                                    sortBy={sortBy}
                                    handleSort={handleSort}
                                    sortable={true}
                                    columnWidth={machineNoteColumnWidth}
                                    onColumnWidthChange={handleColumnWidthChange}
                                />
                                <CustomHeaderCell backgroundColor='#F6F6F6' />
                            </Box>
                            <Box className='tableBody'>
                                <FixedSizeList
                                    ref={scrollRef}
                                    height={Math.min(
                                        notes.filteredNotesCount * 70 + 15,
                                        windowHeight - 400
                                    )}
                                    width='100%'
                                    itemCount={notes.filteredNotesCount}
                                    itemSize={70}
                                    itemData={{ notes, handleNoteClick }}
                                    onScroll={handleScrollList}
                                >
                                    {Row}
                                </FixedSizeList>
                            </Box>
                        </Box>
                    </Box>
            }

            {openAddNote &&
                <AddMachineNote
                    openAddNote={openAddNote}
                    setOpenAddNote={setOpenAddNote}
                />
            }
        </Box>
        </ListNavigationProvider>
    )
}

export default ReadMachineNotesTab